import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminGetOrders, adminForceComplete, adminForceCancel, adminGetClients } from '../../api';

const badge = (status) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
    background: status === 'completed' ? '#d1fae5' : status === 'processing' ? '#dbeafe' : status === 'pending' ? '#fef3c7' : status === 'canceled' ? '#f3f4f6' : status === 'partial' ? '#fef3c7' : '#fee2e2',
    color:      status === 'completed' ? '#065f46' : status === 'processing' ? '#1d4ed8' : status === 'pending' ? '#92400e' : status === 'canceled' ? '#6b7280' : status === 'partial' ? '#92400e' : '#991b1b',
});

const s = {
    h1:    { fontSize: 22, fontWeight: 700, marginBottom: 20 },
    grid:  { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 },  // className: og-grid-4
    card:  { background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
    cLbl:  { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: .5 },
    cVal:  { fontSize: 24, fontWeight: 700, marginTop: 4 },
    filter:{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' },
    sel:   { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' },
    inp:   { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: 120 },
    panel: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'fixed' },
    th:    { textAlign: 'left', padding: '7px 8px', background: '#f3f4f6', borderBottom: '2px solid #d1d5db', color: '#4b5563', fontSize: 10, textTransform: 'uppercase', whiteSpace: 'nowrap', fontWeight: 700 },
    td:    { padding: '7px 8px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'middle' },
    actBtn:{ padding: '3px 8px', border: '1px solid', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600, background: 'none' },
    // Bulk action bar
    bulkBar: { display: 'flex', gap: 10, alignItems: 'center', padding: '10px 16px', background: '#eef2ff', borderBottom: '1px solid #c7d2fe', fontSize: 13, color: '#4338ca' },
    bulkBtn: { padding: '5px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    // Modal styles
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
    modal: { background: '#fff', borderRadius: 14, padding: '28px 32px', maxWidth: 500, width: '90%', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' },
    mTitle: { fontSize: 18, fontWeight: 700, color: '#1a1d23', marginBottom: 4 },
    mSub: { fontSize: 13, color: '#6b7280', marginBottom: 20 },
    mLabel: { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 16 },
    mRadioRow: { display: 'flex', gap: 12, marginBottom: 4 },
    mRadio: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', color: '#374151' },
    mInput: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' },
    mTextarea: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, minHeight: 60, resize: 'vertical', boxSizing: 'border-box' },
    mBtnRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 },
    mBtnCancel: { padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    mBtnConfirm: { padding: '10px 20px', border: 'none', borderRadius: 8, background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
};

const STATUSES = ['all','pending','processing','completed','partial','canceled','error'];

export default function AdminOrders() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [orders, setOrders]   = useState([]);
    const [status, setStatus]   = useState(searchParams.get('status') || 'all');
    const [clientId, setClientId] = useState(searchParams.get('client_id') || '');
    const [search, setSearch]   = useState(searchParams.get('q') || '');
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    // Client autocomplete
    const [allClients, setAllClients] = useState([]);
    const [clientSearch, setClientSearch] = useState(searchParams.get('client') || '');
    const [showClientDropdown, setShowClientDropdown] = useState(false);

    // Sync filters to URL params
    useEffect(() => {
        const p = {};
        if (status !== 'all') p.status = status;
        if (clientId) { p.client_id = clientId; p.client = clientSearch; }
        if (search) p.q = search;
        setSearchParams(p, { replace: true });
    }, [status, clientId, search]); // eslint-disable-line

    React.useEffect(() => {
        adminGetClients().then(r => setAllClients(r.data || [])).catch(() => {});
    }, []);

    const filteredClients = clientSearch.length > 0
        ? allClients.filter(c =>
            c.username?.toLowerCase().includes(clientSearch.toLowerCase()) ||
            c.email?.toLowerCase().includes(clientSearch.toLowerCase()) ||
            String(c.id) === clientSearch
        ).slice(0, 8)
        : [];

    const selectClient = (c) => {
        setClientId(String(c.id));
        setClientSearch(c.username);
        setShowClientDropdown(false);
    };

    const clearClient = () => {
        setClientId('');
        setClientSearch('');
        setShowClientDropdown(false);
    };

    // Bulk selection
    const [selected, setSelected] = useState(new Set());

    // Cancel modal state
    const [cancelModal, setCancelModal] = useState(null);
    const [cancelStatus, setCancelStatus] = useState('canceled');
    const [cancelRefund, setCancelRefund] = useState('auto');
    const [customRefund, setCustomRefund] = useState('');
    const [cancelReason, setCancelReason] = useState('');

    const load = () => {
        setLoading(true);
        setSelected(new Set());
        const params = { limit: 1000 };
        if (status !== 'all') params.status = status;
        if (clientId) params.client_id = clientId;
        adminGetOrders(params)
            .then(r => setOrders(Array.isArray(r.data) ? r.data : []))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(load, [status, clientId]); // eslint-disable-line

    // Bulk helpers
    const actionableOrders = (list) => list.filter(o => ['pending', 'processing'].includes(o.status));
    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleSelectAll = () => {
        const actionable = actionableOrders(displayOrders);
        if (selected.size === actionable.length && actionable.length > 0) {
            setSelected(new Set());
        } else {
            setSelected(new Set(actionable.map(o => o.id)));
        }
    };

    const bulkComplete = async () => {
        if (!selected.size) return;
        if (!window.confirm(`Force complete ${selected.size} orders?`)) return;
        setActionLoading('bulk');
        try {
            for (const id of selected) {
                await adminForceComplete(id);
            }
            setSelected(new Set());
            load();
        } catch (e) {
            alert('Bulk complete failed: ' + (e.response?.data?.error || e.message));
        } finally {
            setActionLoading(null);
        }
    };

    const bulkCancel = async () => {
        if (!selected.size) return;
        if (!window.confirm(`Force cancel ${selected.size} orders with auto-refund?`)) return;
        setActionLoading('bulk');
        try {
            for (const id of selected) {
                await adminForceCancel(id, { status: 'canceled' });
            }
            setSelected(new Set());
            load();
        } catch (e) {
            alert('Bulk cancel failed: ' + (e.response?.data?.error || e.message));
        } finally {
            setActionLoading(null);
        }
    };

    const forceComplete = async (id) => {
        if (!window.confirm(`Force complete order #${id}? This marks it as done and sets remains=0.`)) return;
        setActionLoading(id);
        try {
            await adminForceComplete(id);
            load();
        } catch (e) {
            alert('Failed: ' + (e.response?.data?.error || e.message));
        } finally {
            setActionLoading(null);
        }
    };

    const openCancelModal = (order) => {
        setCancelModal(order);
        setCancelStatus('canceled');
        setCancelRefund('auto');
        setCustomRefund('');
        setCancelReason('');
    };

    const getOrderProgress = (o) => {
        const remains = o.remains ?? o.quantity;
        const delivered = o.quantity - remains;
        const pct = o.quantity > 0 ? Math.round((delivered / o.quantity) * 100) : 0;
        return { delivered, remains, pct };
    };

    const getAutoRefund = (o) => {
        const remains = o.remains ?? o.quantity;
        const progress = (o.quantity - remains) / o.quantity;
        const minutesSince = (new Date() - new Date(o.created_at)) / 60000;
        if (minutesSince <= 15 && progress === 0) return parseFloat(o.price);
        if (progress < 0.5) return parseFloat((o.price * 0.5).toFixed(4));
        return 0;
    };

    const doForceCancel = async () => {
        if (!cancelModal) return;
        setActionLoading(cancelModal.id);
        try {
            const options = {
                status: cancelStatus,
                ...(cancelReason && { reason: cancelReason }),
            };
            if (cancelRefund === 'custom') {
                options.custom_refund = parseFloat(customRefund) || 0;
            } else if (cancelRefund !== 'auto') {
                options.refund_percent = parseInt(cancelRefund);
            }
            const r = await adminForceCancel(cancelModal.id, options);
            alert(`Cancelled. Refunded: \u20AC${r.data.refunded?.toFixed(4) || '0.0000'}`);
            setCancelModal(null);
            load();
        } catch (e) {
            alert('Failed: ' + (e.response?.data?.error || e.message));
        } finally {
            setActionLoading(null);
        }
    };

    // Filter out subscriptions (they have their own admin page now)
    const nonSubOrders = orders.filter(o => o.category !== 'Subscription' && !(o.service_name || '').toLowerCase().includes('subscription'));

    // Client-side search filter
    const sq = search.toLowerCase();
    const displayOrders = sq
        ? nonSubOrders.filter(o =>
            o.username?.toLowerCase().includes(sq) ||
            o.link?.toLowerCase().includes(sq) ||
            o.service_name?.toLowerCase().includes(sq) ||
            o.category?.toLowerCase().includes(sq) ||
            String(o.id).includes(sq) ||
            String(o.client_id).includes(sq))
        : nonSubOrders;

    const actionableDisplayed = actionableOrders(displayOrders);
    const allActionableSelected = actionableDisplayed.length > 0 && selected.size === actionableDisplayed.length;

    return (
        <div>
            <h1 style={s.h1}>All Orders</h1>

            {/* Stats bar */}
            {nonSubOrders.length > 0 && (() => {
                const total = nonSubOrders.length;
                const byStatus = nonSubOrders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});
                const totalRevenue = nonSubOrders.reduce((acc, o) => acc + parseFloat(o.price || 0), 0);
                const totalRefunds = nonSubOrders.reduce((acc, o) => acc + parseFloat(o.refund_amount || 0), 0);
                return (
                    <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                        <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                            <span style={{ color: '#6b7280' }}>Total:</span> <strong>{total}</strong>
                        </div>
                        {byStatus.processing > 0 && <div style={{ background: '#dbeafe', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                            <span style={{ color: '#1d4ed8' }}>Processing:</span> <strong>{byStatus.processing}</strong>
                        </div>}
                        {byStatus.completed > 0 && <div style={{ background: '#d1fae5', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                            <span style={{ color: '#065f46' }}>Completed:</span> <strong>{byStatus.completed}</strong>
                        </div>}
                        {byStatus.pending > 0 && <div style={{ background: '#fef3c7', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                            <span style={{ color: '#92400e' }}>Pending:</span> <strong>{byStatus.pending}</strong>
                        </div>}
                        {byStatus.partial > 0 && <div style={{ background: '#fef3c7', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                            <span style={{ color: '#92400e' }}>Partial:</span> <strong>{byStatus.partial}</strong>
                        </div>}
                        {byStatus.canceled > 0 && <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                            <span style={{ color: '#6b7280' }}>Canceled:</span> <strong>{byStatus.canceled}</strong>
                        </div>}
                        <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                            <span style={{ color: '#065f46' }}>Revenue:</span> <strong>&euro;{totalRevenue.toFixed(2)}</strong>
                        </div>
                        {totalRefunds > 0 && <div style={{ background: '#fef2f2', borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                            <span style={{ color: '#991b1b' }}>Refunds:</span> <strong>&euro;{totalRefunds.toFixed(2)}</strong>
                        </div>}
                    </div>
                );
            })()}

            <div className="og-filters" style={s.filter}>
                <select style={s.sel} value={status} onChange={e => setStatus(e.target.value)}>
                    {STATUSES.map(st => <option key={st} value={st}>{st.charAt(0).toUpperCase() + st.slice(1)}</option>)}
                </select>
                <div style={{ position: 'relative' }}>
                    <input
                        style={{ ...s.inp, width: 170 }}
                        type="text"
                        placeholder="Client name or ID..."
                        value={clientSearch}
                        onChange={e => { setClientSearch(e.target.value); setShowClientDropdown(true); if (!e.target.value) clearClient(); }}
                        onFocus={() => clientSearch && setShowClientDropdown(true)}
                        onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
                    />
                    {clientId && (
                        <span
                            onClick={clearClient}
                            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#9ca3af', fontSize: 14 }}
                        >&times;</span>
                    )}
                    {showClientDropdown && filteredClients.length > 0 && (
                        <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                            background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2
                        }}>
                            {filteredClients.map(c => (
                                <div
                                    key={c.id}
                                    onMouseDown={() => selectClient(c)}
                                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                >
                                    <span style={{ fontWeight: 600 }}>{c.username}</span>
                                    <span style={{ color: '#9ca3af', fontSize: 10 }}>#{c.id} &middot; &euro;{parseFloat(c.balance || 0).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <input style={{ ...s.inp, width: 200 }} type="text" placeholder="Search user, link, service..."
                    value={search} onChange={e => setSearch(e.target.value)} />
                <button onClick={load} style={{ padding: '8px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                    Filter
                </button>
                {(status !== 'all' || clientId || search) && (
                    <button
                        onClick={() => { setStatus('all'); clearClient(); setSearch(''); }}
                        style={{ padding: '8px 14px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
                    >
                        Clear Filters
                    </button>
                )}
                {search && <span style={{ fontSize: 12, color: '#6b7280' }}>{displayOrders.length} / {orders.length}</span>}
            </div>

            <div style={s.panel}>
                {/* Bulk action bar */}
                {selected.size > 0 && (
                    <div style={s.bulkBar}>
                        <span style={{ fontWeight: 600 }}>{selected.size} selected</span>
                        <button
                            style={{ ...s.bulkBtn, background: '#059669', color: '#fff' }}
                            disabled={actionLoading === 'bulk'}
                            onClick={bulkComplete}
                        >
                            Complete All
                        </button>
                        <button
                            style={{ ...s.bulkBtn, background: '#dc2626', color: '#fff' }}
                            disabled={actionLoading === 'bulk'}
                            onClick={bulkCancel}
                        >
                            Cancel All
                        </button>
                        <button
                            style={{ ...s.bulkBtn, background: '#fff', color: '#6b7280', border: '1px solid #d1d5db' }}
                            onClick={() => setSelected(new Set())}
                        >
                            Clear
                        </button>
                    </div>
                )}

                {loading ? <p style={{ padding: 24, color: '#9ca3af' }}>Loading...</p> : (
                    <div className="og-table-wrap">
                    <table style={s.table}>
                        <colgroup>
                            <col style={{ width: 32 }} />{/* checkbox */}
                            <col style={{ width: 60 }} />{/* # id */}
                            <col style={{ width: 90 }} />{/* Client */}
                            <col style={{ width: 200 }} />{/* Service */}
                            <col style={{ width: 180 }} />{/* Link */}
                            <col style={{ width: 60 }} />{/* Qty */}
                            <col style={{ width: 110 }} />{/* Status */}
                            <col style={{ width: 140 }} />{/* Progress */}
                            <col style={{ width: 80 }} />{/* Price */}
                            <col style={{ width: 105 }} />{/* Created */}
                            <col style={{ width: 85 }} />{/* Actions */}
                        </colgroup>
                        <thead>
                            <tr>
                                <th style={{ ...s.th, textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={allActionableSelected}
                                        onChange={toggleSelectAll}
                                        title="Select all actionable orders"
                                    />
                                </th>
                                {['#','Client','Service','Link','Qty','Status','Progress','Price','Created','Actions'].map(h =>
                                    <th key={h} style={s.th}>{h}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {displayOrders.map(o => {
                                const isActionable = ['pending', 'processing'].includes(o.status);
                                const isChecked = selected.has(o.id);

                                const rowIdx = displayOrders.indexOf(o);
                                return (
                                    <tr key={o.id} style={isChecked ? { background: '#eef2ff' } : (rowIdx % 2 === 0 ? undefined : { background: '#fafbfc' })}>
                                        <td style={{ ...s.td, textAlign: 'center' }}>
                                            {isActionable && (
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => toggleSelect(o.id)}
                                                />
                                            )}
                                        </td>
                                        <td style={s.td}>{o.id}</td>
                                        <td style={{ ...s.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={o.username || o.client_id}>{o.username || o.client_id}</td>
                                        <td style={{ ...s.td, maxWidth: 180, wordBreak: 'break-word' }}>
                                            <div style={{ fontSize: 11, color: '#1f2937', lineHeight: 1.3 }}>
                                                {o.service_name?.replace(/instagram\s*[-\u2013]/i,'').trim() || o.service_id}
                                            </div>
                                            <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 1, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                                {o.category && <span>{o.category}</span>}
                                                {o.service_id && <span>&middot; #{o.service_id}</span>}
                                                <span style={{
                                                    padding: '0 4px', borderRadius: 3, fontSize: 8, fontWeight: 700, letterSpacing: 0.3,
                                                    background: o.order_channel === 'api' ? '#fef3c7' : '#dbeafe',
                                                    color: o.order_channel === 'api' ? '#92400e' : '#1d4ed8',
                                                }}>
                                                    {o.order_channel === 'api' ? 'API' : 'WEB'}
                                                </span>
                                            </div>
                                        </td>
                                        <td style={{ ...s.td, maxWidth: 130, wordBreak: 'break-all' }}>
                                            {o.link ? (
                                                <a
                                                    href={o.link.startsWith('http') ? o.link : `https://instagram.com/${o.link}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#4f46e5', textDecoration: 'none', fontSize: 11 }}
                                                >
                                                    {o.link} <span style={{ fontSize: 10 }}>&#x2197;</span>
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td style={s.td}>{o.quantity}</td>
                                        <td style={s.td} title={['canceled', 'partial'].includes(o.status) && o.cancel_note ? `${o.refund_amount > 0 ? '€' + parseFloat(o.refund_amount).toFixed(2) + ' — ' : ''}${o.cancel_note}` : ''}>
                                            <span style={badge(o.status)}>
                                                {o.status}
                                            </span>
                                            {['canceled', 'partial'].includes(o.status) && (o.cancel_note || o.canceled_by === 'client') && (
                                                <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2, lineHeight: 1.3, maxWidth: 110, wordBreak: 'break-word' }}>
                                                    {o.canceled_by === 'client' && <span style={{ color: '#dc2626', fontWeight: 600 }}>by user </span>}
                                                    {o.cancel_note && `${o.canceled_by === 'admin' ? '[A] ' : ''}${o.cancel_note}`}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ ...s.td, minWidth: 100 }}>
                                            {(() => {
                                                const isSub = o.category === 'Subscription' || (o.service_name || '').toLowerCase().includes('subscription');
                                                const remains = o.remains ?? o.quantity;
                                                const delivered = o.quantity - remains;
                                                const pct = o.quantity > 0 ? Math.round((delivered / o.quantity) * 100) : 0;
                                                const target = o.start_count ? o.start_count + o.quantity : null;

                                                if (isSub) {
                                                    const hoursSince = (Date.now() - new Date(o.created_at)) / 3600000;
                                                    const inGracePeriod = hoursSince < 24 && ['pending', 'processing'].includes(o.status);

                                                    if (inGracePeriod) {
                                                        return (
                                                            <div style={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>
                                                                Starts in {Math.ceil(24 - hoursSince)}h
                                                            </div>
                                                        );
                                                    }

                                                    if (o.subscription_end_date) {
                                                        const start = new Date(o.subscription_start_date || o.created_at);
                                                        const end = new Date(o.subscription_end_date);
                                                        const now = new Date();
                                                        const totalDays = Math.max(1, Math.round((end - start) / 86400000));
                                                        const elapsed = Math.min(totalDays, Math.max(1, Math.floor((now - start) / 86400000) + 1));
                                                        const daysPct = Math.min(100, Math.round((elapsed / totalDays) * 100));
                                                        const isExpired = now > end;
                                                        return (
                                                            <div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#374151', marginBottom: 2 }}>
                                                                    <span style={{ fontWeight: 600 }}>Day {elapsed}/{totalDays}</span>
                                                                    <span style={{ color: isExpired ? '#dc2626' : '#9ca3af' }}>
                                                                        {isExpired ? 'Expired' : `Ends ${new Date(end.getTime() - 86400000).toLocaleDateString()}`}
                                                                    </span>
                                                                </div>
                                                                <div style={{ background: '#e5e7eb', borderRadius: 3, height: 4, overflow: 'hidden' }}>
                                                                    <div style={{ background: isExpired ? '#dc2626' : '#8b5cf6', height: '100%', width: `${daysPct}%`, borderRadius: 3 }} />
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    return <div style={{ fontSize: 11, color: '#6b7280' }}>Subscription</div>;
                                                }

                                                const lastAct = o.last_action_at ? new Date(o.last_action_at) : null;
                                                const diffMs = lastAct ? Date.now() - lastAct : null;
                                                const lastActLabel = diffMs != null ? (() => {
                                                    const m = Math.round(diffMs / 60000);
                                                    if (m < 1)  return 'just now';
                                                    if (m < 60) return `${m}min ago`;
                                                    const h = Math.floor(m / 60);
                                                    if (h < 24) return `${h}h ago`;
                                                    return `${Math.floor(h / 24)}d ago`;
                                                })() : null;
                                                const hoursAgo = diffMs != null ? diffMs / 3600000 : 0;

                                                return (
                                                    <div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#374151', marginBottom: 2 }}>
                                                            <span style={{ fontWeight: 600 }}>{pct}%</span>
                                                            <span style={{ color: '#9ca3af' }}>
                                                                {delivered}/{o.quantity}
                                                                {(() => {
                                                                    const overcapDone = Math.max(0, (o.completed_count || 0) - o.quantity);
                                                                    if (overcapDone <= 0) return null;
                                                                    return (
                                                                        <span style={{ color: '#f59e0b', fontWeight: 600, marginLeft: 4, fontSize: 9 }} title={`Overdelivery: +${overcapDone} extra delivered (exec_quantity includes HikerAPI retry budget — not meaningful as overdelivery target)`}>
                                                                            +{overcapDone}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </span>
                                                        </div>
                                                        <div style={{ background: '#e5e7eb', borderRadius: 3, height: 4, overflow: 'hidden' }}>
                                                            <div style={{ background: pct >= 100 ? '#22c55e' : '#6366f1', height: '100%', width: `${Math.min(pct, 100)}%`, borderRadius: 3 }} />
                                                        </div>
                                                        {target && (
                                                            <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 1 }} title="Start count available for: Follow, Like, Comment services only">
                                                                Start: {o.start_count} &rarr; Target: {target}
                                                            </div>
                                                        )}
                                                        {lastAct && ['processing', 'pending'].includes(o.status) && (
                                                            <div style={{ fontSize: 9, color: hoursAgo >= 6 ? '#dc2626' : '#6b7280', marginTop: 2 }}
                                                                 title={lastAct.toISOString()}>
                                                                Last action: {lastActLabel} ({lastAct.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' })})
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td style={s.td}>
                                            <div>&euro;{parseFloat(o.price).toFixed(4)}</div>
                                            {o.refund_amount > 0 && (
                                                <div style={{ fontSize: 10, color: '#059669', fontWeight: 600, marginTop: 2 }}
                                                     title={`Refund: €${parseFloat(o.refund_amount).toFixed(4)}`}>
                                                    refund &euro;{parseFloat(o.refund_amount).toFixed(2)}
                                                </div>
                                            )}
                                        </td>
                                        <td style={s.td}>{new Date(o.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' })}</td>
                                        <td style={s.td}>
                                            {o.internal_order_id && (
                                                <div style={{ fontSize: 9, color: '#9ca3af', marginBottom: 2 }} title="OM ID">
                                                    OM {o.internal_order_id}
                                                </div>
                                            )}
                                            {isActionable && (
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    <button
                                                        style={{ ...s.actBtn, color: '#059669', borderColor: '#a7f3d0' }}
                                                        disabled={actionLoading === o.id}
                                                        onClick={() => forceComplete(o.id)}
                                                        title="Force complete this order">
                                                        &#10003;
                                                    </button>
                                                    <button
                                                        style={{ ...s.actBtn, color: '#dc2626', borderColor: '#fecaca' }}
                                                        disabled={actionLoading === o.id}
                                                        onClick={() => openCancelModal(o)}
                                                        title="Force cancel + refund">
                                                        &#10005;
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>

            {/* Admin Cancel Modal */}
            {cancelModal && (
                <div style={s.overlay} onClick={() => setCancelModal(null)}>
                    <div style={s.modal} onClick={e => e.stopPropagation()}>
                        <div style={s.mTitle}>Cancel Order #{cancelModal.id}</div>
                        <div style={s.mSub}>
                            {cancelModal.service_name} &middot; {cancelModal.username || `Client #${cancelModal.client_id}`}
                        </div>

                        {/* Progress info */}
                        {(() => {
                            const p = getOrderProgress(cancelModal);
                            return (
                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', marginBottom: 16, border: '1px solid #e5e7eb' }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                                        Progress: {p.pct}% ({p.delivered}/{cancelModal.quantity} delivered)
                                    </div>
                                    <div style={{ background: '#e5e7eb', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                                        <div style={{ background: '#6366f1', height: '100%', width: `${p.pct}%`, borderRadius: 4 }} />
                                    </div>
                                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                        Auto-suggested refund: &euro;{getAutoRefund(cancelModal).toFixed(4)}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Status */}
                        <div style={s.mLabel}>Status</div>
                        <div style={s.mRadioRow}>
                            {['canceled', 'partial'].map(st => (
                                <label key={st} style={s.mRadio}>
                                    <input type="radio" name="cancelStatus" value={st}
                                        checked={cancelStatus === st} onChange={() => setCancelStatus(st)} />
                                    {st.charAt(0).toUpperCase() + st.slice(1)}
                                </label>
                            ))}
                        </div>

                        {/* Refund */}
                        <div style={s.mLabel}>Refund</div>
                        <div style={{ ...s.mRadioRow, flexWrap: 'wrap' }}>
                            {[
                                { val: 'auto', label: 'Auto (rules)' },
                                { val: '100', label: '100%' },
                                { val: '50', label: '50%' },
                                { val: '0', label: '0%' },
                                { val: 'custom', label: 'Custom' },
                            ].map(opt => (
                                <label key={opt.val} style={s.mRadio}>
                                    <input type="radio" name="cancelRefund" value={opt.val}
                                        checked={cancelRefund === opt.val} onChange={() => setCancelRefund(opt.val)} />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                        {cancelRefund === 'custom' && (
                            <input
                                type="number" step="0.01" min="0" max={parseFloat(cancelModal.price)}
                                placeholder={`Max: \u20AC${parseFloat(cancelModal.price).toFixed(4)}`}
                                value={customRefund} onChange={e => setCustomRefund(e.target.value)}
                                style={{ ...s.mInput, marginTop: 8 }}
                            />
                        )}

                        {/* Reason */}
                        <div style={s.mLabel}>Reason / Note (optional)</div>
                        <textarea
                            style={s.mTextarea}
                            placeholder="Reason for cancellation..."
                            value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                        />

                        <div style={s.mBtnRow}>
                            <button style={s.mBtnCancel} onClick={() => setCancelModal(null)}>Dismiss</button>
                            <button
                                style={{ ...s.mBtnConfirm, opacity: actionLoading ? 0.6 : 1 }}
                                disabled={!!actionLoading}
                                onClick={doForceCancel}
                            >
                                Cancel Order
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
